/**
 * Fahrer View - Mobile-optimierte Ansicht für Fahrer
 * 
 * URL: /fahrer
 * 
 * Features:
 * - Login mit E-Mail/Passwort
 * - Aktive Lieferungen anzeigen
 * - Navigation zu Adresse (Google Maps)
 * - Als geliefert markieren
 * - Tagesübersicht
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Bike, MapPin, Phone, Navigation, CheckCircle, Package,
  LogOut, RefreshCw, Euro, Clock, User, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FahrerView() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('orders');
  const [settlement, setSettlement] = useState(null);

  // Check existing session
  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    const info = localStorage.getItem('driver_info');
    
    if (token && info) {
      setIsLoggedIn(true);
      setDriverInfo(JSON.parse(info));
    }
    setLoading(false);
  }, []);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem('driver_token');
    if (!token) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/driver/orders/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      } else if (res.status === 401) {
        // Session expired
        handleLogout();
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  }, []);

  // Fetch settlement
  const fetchSettlement = useCallback(async () => {
    const token = localStorage.getItem('driver_token');
    const info = driverInfo || JSON.parse(localStorage.getItem('driver_info') || '{}');
    if (!token || !info?.driver_id) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/dispatch/settlement/${info.driver_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSettlement(data);
      }
    } catch (err) {
      console.error('Error fetching settlement:', err);
    }
  }, [driverInfo]);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchOrders();
      fetchSettlement();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchOrders, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, fetchOrders, fetchSettlement]);

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/team/driver-login?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`, {
        method: 'POST'
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('driver_token', data.token);
        localStorage.setItem('driver_info', JSON.stringify({
          driver_id: data.driver_id,
          name: data.driver_name,
          email: data.email,
          tenant_id: data.tenant_id
        }));
        setDriverInfo({
          driver_id: data.driver_id,
          name: data.driver_name,
          email: data.email
        });
        setIsLoggedIn(true);
        toast.success(`Willkommen, ${data.driver_name}!`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Login fehlgeschlagen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setLoginLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_info');
    setIsLoggedIn(false);
    setDriverInfo(null);
    setOrders([]);
    toast.success('Abgemeldet');
  };

  // Mark as delivered
  const markDelivered = async (orderId) => {
    const token = localStorage.getItem('driver_token');
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/driver/orders/${orderId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'delivered' })
      });

      if (res.ok) {
        toast.success('Als geliefert markiert ✓');
        fetchOrders();
        fetchSettlement();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  // Generate maps URL
  const getMapsUrl = (order) => {
    const addr = order.delivery_address;
    if (typeof addr === 'object' && addr?.lat && addr?.lng) {
      return `https://www.google.com/maps/search/?api=1&query=${addr.lat},${addr.lng}`;
    }
    const addrStr = typeof addr === 'string' ? addr : 
      `${addr?.street || ''} ${addr?.house_number || ''}, ${addr?.postal_code || ''} ${addr?.city || ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrStr)}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div 
        className="min-h-screen bg-slate-900 flex items-center justify-center p-4"
        data-testid="driver-login-screen"
      >
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto mb-4">
              <Bike className="w-8 h-8 text-teal-400" />
            </div>
            <CardTitle className="text-white text-2xl">Fahrer Login</CardTitle>
            <p className="text-slate-400 text-sm mt-2">Cash-X Liefersystem</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="E-Mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="driver-email-input"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                  data-testid="driver-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-teal-500 hover:bg-teal-400 text-white h-12"
                data-testid="driver-login-btn"
              >
                {loginLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  'Anmelden'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Driver View
  return (
    <div 
      className="min-h-screen bg-slate-900 pb-20"
      data-testid="driver-main-view"
    >
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <p className="font-semibold text-white">{driverInfo?.name || 'Fahrer'}</p>
              <p className="text-xs text-slate-400">{orders.length} aktive Lieferungen</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchOrders}
              className="text-slate-400"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-700">
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
            activeTab === 'orders'
              ? 'text-teal-400 border-b-2 border-teal-400'
              : 'text-slate-400'
          }`}
        >
          <Package className="w-4 h-4 inline mr-1" />
          Lieferungen
        </button>
        <button
          onClick={() => setActiveTab('summary')}
          className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
            activeTab === 'summary'
              ? 'text-teal-400 border-b-2 border-teal-400'
              : 'text-slate-400'
          }`}
        >
          <Euro className="w-4 h-4 inline mr-1" />
          Abrechnung
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'orders' ? (
          // Orders List
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">Keine aktiven Lieferungen</p>
                <p className="text-xs text-slate-500 mt-1">
                  Neue Bestellungen erscheinen automatisch
                </p>
              </div>
            ) : (
              orders.map((order) => {
                const addr = order.delivery_address;
                const addrStr = typeof addr === 'string' ? addr :
                  `${addr?.street || ''} ${addr?.house_number || ''}, ${addr?.postal_code || ''} ${addr?.city || ''}`;
                const phone = order.customer_phone;

                return (
                  <Card 
                    key={order._id || order.order_id}
                    className="bg-slate-800 border-slate-700"
                    data-testid="driver-order-card"
                  >
                    <CardContent className="p-4">
                      {/* Order Header */}
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30">
                          #{order.order_number || order.transaction_id?.slice(-6)}
                        </Badge>
                        <span className="text-xl font-bold text-white">
                          {(order.total_gross || order.total || 0).toFixed(2)}€
                        </span>
                      </div>

                      {/* Customer */}
                      <p className="text-white font-medium mb-2">
                        {order.customer_name || 'Kunde'}
                      </p>

                      {/* Address */}
                      <div className="flex items-start gap-2 text-slate-300 mb-3">
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                        <span className="text-sm">{addrStr}</span>
                      </div>

                      {/* Notes */}
                      {order.notes && (
                        <div className="flex items-start gap-2 text-amber-400 mb-3 p-2 bg-amber-500/10 rounded">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{order.notes}</span>
                        </div>
                      )}

                      {/* Payment Method */}
                      <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
                        <Euro className="w-4 h-4" />
                        <span>
                          {order.payment_method === 'bar' || order.payment_method === 'cash'
                            ? 'Bar bei Lieferung'
                            : 'Bereits bezahlt'}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => window.open(getMapsUrl(order), '_blank')}
                          className="bg-blue-500 hover:bg-blue-400 text-white"
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Navigation
                        </Button>
                        
                        {phone && (
                          <Button
                            onClick={() => window.location.href = `tel:${phone.replace(/\s/g, '')}`}
                            variant="outline"
                            className="border-slate-600 text-slate-300"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Anrufen
                          </Button>
                        )}
                      </div>

                      <Button
                        onClick={() => markDelivered(order._id || order.order_id)}
                        className="w-full mt-3 bg-emerald-500 hover:bg-emerald-400 text-white h-12"
                        data-testid="mark-delivered-btn"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Als geliefert markieren
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        ) : (
          // Settlement Summary
          <div className="space-y-4">
            {settlement ? (
              <>
                {/* Summary Card */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">
                      Heute - {settlement.date}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-xs text-slate-400">Lieferungen</p>
                        <p className="text-2xl font-bold text-white">
                          {settlement.summary?.total_deliveries || 0}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <p className="text-xs text-slate-400">Bestellwert</p>
                        <p className="text-2xl font-bold text-white">
                          {(settlement.summary?.total_order_value || 0).toFixed(2)}€
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-slate-700 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Bargeld eingenommen</span>
                        <span className="text-white font-medium">
                          {(settlement.summary?.total_cash_collected || 0).toFixed(2)}€
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Trinkgeld</span>
                        <span className="text-emerald-400 font-medium">
                          +{(settlement.summary?.total_tips || 0).toFixed(2)}€
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-slate-700">
                        <span className="text-slate-300 font-medium">Abzugeben</span>
                        <span className="text-amber-400 font-bold text-lg">
                          {(settlement.summary?.amount_due_to_restaurant || 0).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Orders List */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Gelieferte Bestellungen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {settlement.orders?.length > 0 ? (
                      <div className="space-y-2">
                        {settlement.orders.map((order, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-2 bg-slate-900/50 rounded"
                          >
                            <div>
                              <p className="text-white text-sm">
                                #{order.order_number?.slice(-6)}
                              </p>
                              <p className="text-xs text-slate-400">
                                {order.customer_name}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">
                                {(order.cash_collected || 0).toFixed(2)}€
                              </p>
                              {order.tip > 0 && (
                                <p className="text-xs text-emerald-400">
                                  +{order.tip.toFixed(2)}€ TG
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-400 py-4">
                        Noch keine Lieferungen heute
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">Lade Abrechnung...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
